import { useEffect, useState, memo } from "react";

import { Button } from "primereact/button";
import { Chart } from "primereact/chart";

import { Chart as ChartJS } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import annotationPlugin from "chartjs-plugin-annotation";

ChartJS.register(annotationPlugin);

const colors = [
  "#2ecc71",
  "#2980b9",
  "#f1c40f",
  "#8e44ad",
  "#e67e22",
  "#16a085",
  "#c0392b",
  "#2c3e50",
];

export default memo(function RevisaoGraficoSetorPorcentagem({
  data,
  months,
  setores,
  setor,
  chartType,
  fieldValor = "porcentagem_revisado",
}) {
  const [chartData, setChartData] = useState({});
  const [col, setCol] = useState(
    chartType === "revisado" ? "p-col-12" : "p-col-6"
  );
  const [type, setType] = useState("bar");
  const [meta, setMeta] = useState(0);

  const isStacked = chartType === "revisado" && setor === -1;

  const formatPct = (v) =>
    Intl.NumberFormat("pt-BR", { maximumFractionDigits: 2 }).format(
      Number(v || 0)
    ) + "%";

  function getTotalMes(mesNumero) {
    const found = data.find(
      (e) =>
        parseInt(e.mes) === mesNumero &&
        e.cod_defeitotipo === -1 &&
        e.setor === "Total"
    );
    return found ? Number(found[fieldValor] || 0) : 0;
  }

  function getTotalPeriodo() {
    const found13 = data.find(
      (e) =>
        (Number(e.mes) === 13 || String(e.mes) === "13") &&
        e.cod_defeitotipo === -1 &&
        e.setor === "Total"
    );
    return found13 ? Number(found13[fieldValor] || 0) : 0;
  }

  const options = {
    maintainAspectRatio: false,
    scales: isStacked
      ? {
          x: { stacked: true },
          y: { stacked: true },
        }
      : undefined,
    plugins: {
      datalabels: {
        display: (ctx) => {
          const v = ctx.dataset.data?.[ctx.dataIndex] ?? 0;
          return Number(v) > 0;
        },
        anchor: isStacked ? "center" : "end",
        align: isStacked ? "center" : "end",
        formatter: (value) => formatPct(value),
        font: { size: 12, weight: "bold" },
        color: "#2c3e50",
      },
      annotation: {
        annotations: {
          line1: {
            type: "line",
            yMin: meta,
            yMax: meta,
            borderColor: "rgb(255, 99, 132)",
            borderWidth: 2,
          },
        },
      },
      title: {
        display: true,
        text: isStacked
          ? "Composição do percentual revisado por setor"
          : "Relação Defeitos / Revisado",
        font: { size: 18 },
      },
    },
  };

  useEffect(() => {
    if (!isStacked) {
      const values = [];
      const labels = [];

      for (let i = 0; i < months.length; i++) {
        const mesNumero = i + 1;

        const found = data.find(
          (e) => parseInt(e.mes) === mesNumero && e.cod_defeitotipo === setor
        );

        const v = found ? Number(found[fieldValor] || 0) : 0;

        values.push(v);
        labels.push(`${months[i]} - ${formatPct(v)}`);
      }

      setChartData({
        labels,
        datasets: [
          {
            label: "Relação Defeitos / Revisado",
            data: values,
            backgroundColor: "#2ecc71",
            borderColor: "#2ecc71",
          },
        ],
      });

      const metaSetor = setores.find((s) => s.idtecdefeitotipo === setor);
      setMeta(metaSetor ? Number(metaSetor.qualidade || 0) : 0);
      return;
    }

    const labels = [];
    for (let i = 0; i < months.length; i++) {
      const totalMes = getTotalMes(i + 1);
      labels.push(`${months[i]} - ${formatPct(totalMes)}`);
    }

    const totalPeriodo = getTotalPeriodo();
    labels.push(`TOTAL - ${formatPct(totalPeriodo)}`);

    const valoresPorSetor = setores.map((s) => {
      const arr = [];
      for (let i = 0; i < months.length; i++) {
        const found = data.find(
          (e) => parseInt(e.mes) === i + 1 && e.setor === s.nome
        );
        arr.push(found ? Number(found[fieldValor] || 0) : 0);
      }
      return arr;
    });

    const somaPeriodo = valoresPorSetor.reduce(
      (acc, arr) => acc + arr.reduce((a, v) => a + Number(v || 0), 0),
      0
    );

    const datasets = setores.map((s, idx) => {
      const valores = [...valoresPorSetor[idx]];

      const somaSetor = valores.reduce((a, v) => a + Number(v || 0), 0);
      const proporcao = somaPeriodo > 0 ? somaSetor / somaPeriodo : 0;

      valores.push(Number((totalPeriodo * proporcao).toFixed(6)));

      return {
        label: s.nome,
        data: valores,
        backgroundColor: colors[idx % colors.length],
        borderColor: colors[idx % colors.length],
      };
    });

    setChartData({ labels, datasets });

    const metaTotal = setores.reduce(
      (acc, s) => acc + Number(s.qualidade || 0),
      0
    );
    setMeta(metaTotal);
  }, [isStacked, data, months, setor, setores, fieldValor]);

  return (
    <div className={col}>
      <div className="p-d-flex p-flex-row p-jc-start">
        <Button
          className="p-m-1"
          icon="pi pi-search"
          onClick={() => setCol(col === "p-col-6" ? "p-col-12" : "p-col-6")}
        />
        <Button
          className="p-m-1"
          icon={type === "line" ? "pi pi-chart-line" : "pi pi-chart-bar"}
          onClick={() => setType(type === "line" ? "bar" : "line")}
          disabled={isStacked}
        />
      </div>

      <Chart
        data={chartData}
        height="350px"
        width="100%"
        options={options}
        plugins={[ChartDataLabels]}
        type={isStacked ? "bar" : type}
      />
    </div>
  );
});
